import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { GlassCard, Picker, SearchField } from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";

export const CustomerFilter = ({
  customers,
  users = [],
  unaffiliatedUsers = [],
  onFilterChange,
  initialFilters = {},
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(
    initialFilters.customer || null,
  );
  // Map old filter values to new ones for backward compatibility
  const getInitialStatusFilter = () => {
    const oldValue = initialFilters.hideDeactivated;
    if (oldValue === "show-all" || !oldValue) return ["all"];
    if (oldValue === "hide-deactivated") return ["active"];
    if (oldValue === "show-deactivated-only") return ["deactivated"];
    // Handle array values for multi-selection
    if (Array.isArray(oldValue)) return oldValue;
    return [oldValue]; // Wrap single value in array
  };

  const [statusFilter, setStatusFilter] = useState(getInitialStatusFilter());
  const [selectedRole, setSelectedRole] = useState(initialFilters.role || null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Extract unique roles from all users
  const availableRoles = React.useMemo(() => {
    const roles = new Set();
    users.forEach((user) => {
      if (user.roles && user.roles.roles && Array.isArray(user.roles.roles)) {
        user.roles.roles.forEach((role) => {
          roles.add(role);
        });
      }
    });
    return Array.from(roles).sort();
  }, [users]);

  // Notify parent when filters change
  useEffect(() => {
    onFilterChange({
      searchQuery: debouncedSearchQuery,
      customer: selectedCustomer,
      hideDeactivated: statusFilter,
      role: selectedRole,
    });
  }, [
    debouncedSearchQuery,
    selectedCustomer,
    statusFilter,
    selectedRole,
    onFilterChange,
  ]);

  // Helper function to check if user matches filters
  const userMatchesFilters = (user) => {
    // Search filter
    const matchesSearch =
      !debouncedSearchQuery ||
      user.email_address
        ?.toLowerCase()
        .includes(debouncedSearchQuery.toLowerCase()) ||
      user.first_name
        ?.toLowerCase()
        .includes(debouncedSearchQuery.toLowerCase()) ||
      user.last_name
        ?.toLowerCase()
        .includes(debouncedSearchQuery.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`
        ?.toLowerCase()
        .includes(debouncedSearchQuery.toLowerCase());

    // Status filter - handle multiple selections
    let matchesStatus = true;
    if (
      statusFilter &&
      statusFilter.length > 0 &&
      !statusFilter.includes("all")
    ) {
      matchesStatus = statusFilter.includes(user.status);
    }
    // If statusFilter includes "all" or is empty, matchesStatus stays true (shows all)

    // Role filter
    const matchesRole =
      !selectedRole ||
      (user.roles &&
        user.roles.roles &&
        user.roles.roles.includes(selectedRole));

    return matchesSearch && matchesStatus && matchesRole;
  };

  // Get filtered count
  const filteredCount = React.useMemo(() => {
    let count = 0;

    // Count affiliated users
    customers.forEach((customer) => {
      // Apply customer filter
      if (selectedCustomer && customer.id !== selectedCustomer) {
        return;
      }

      // Count users based on filters
      const clientEmails = customer.clients || [];
      count += clientEmails.filter((clientEmail) => {
        const user = users.find((u) => u.email_address === clientEmail);
        return user && userMatchesFilters(user);
      }).length;
    });

    // Count unaffiliated users (only if no specific customer is selected)
    if (!selectedCustomer) {
      count += unaffiliatedUsers.filter(userMatchesFilters).length;
    }

    return count;
  }, [
    customers,
    users,
    unaffiliatedUsers,
    debouncedSearchQuery,
    selectedCustomer,
    statusFilter,
    selectedRole,
  ]);

  const totalUsers =
    customers.reduce((sum, c) => sum + (c.clients?.length || 0), 0) +
    unaffiliatedUsers.length;

  const statusOptions = [
    { label: "All Users", value: "all" },
    { label: "Active", value: "active" },
    { label: "Deactivated", value: "deactivated" },
    { label: "Invited", value: "invited" },
  ];

  return (
    <GlassCard>
      <FilterContainer>
        {/* First Row: User Count + Search Field */}
        <FirstRow>
          <UserCount>
            <CountNumber>{filteredCount}</CountNumber>
            <CountLabel>
              {filteredCount === 1 ? "User" : "Users"}
              {filteredCount !== totalUsers && ` (of ${totalUsers})`}
            </CountLabel>
          </UserCount>
          <SearchSection>
            <SearchField
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </SearchSection>
        </FirstRow>

        {/* Second Row: Status Multi-Picker + Other Filters */}
        <SecondRow>
          <StatusPickerSection>
            <Picker
              onChange={setStatusFilter}
              value={statusFilter}
              data={statusOptions}
              multiple={true}
              searchable={false}
              placeholder="Select Status"
              cleanable={true}
              native={false}
              width="220px"
            />
          </StatusPickerSection>

          <FiltersSection>
            <FilterGroup>
              <Picker
                onChange={setSelectedCustomer}
                value={selectedCustomer}
                data={customers.map((customer) => ({
                  label: customer.name,
                  value: customer.id,
                }))}
                multiple={false}
                searchable={true}
                placeholder="All Customers"
                cleanable={true}
                native={false}
                width="180px"
              />
            </FilterGroup>

            {availableRoles.length > 0 && (
              <FilterGroup>
                <Picker
                  onChange={setSelectedRole}
                  value={selectedRole}
                  data={availableRoles.map((role) => ({
                    label: role,
                    value: role,
                  }))}
                  multiple={false}
                  searchable={false}
                  placeholder="All Roles"
                  cleanable={true}
                  native={false}
                  width="180px"
                />
              </FilterGroup>
            )}
          </FiltersSection>
        </SecondRow>
      </FilterContainer>
    </GlassCard>
  );
};

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  .rs-picker {
    width: 100%;

    .rs-picker-toggle {
      border-radius: 8px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(8px);
      padding: 6px 12px;
      min-height: 38px !important;
      height: 38px !important;

      &:hover {
        border-color: rgba(255, 255, 255, 0.4);
        background: rgba(255, 255, 255, 0.15);
      }
    }
  }
`;

const FirstRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const UserCount = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 150px;

  @media screen and (max-width: 768px) {
    justify-content: center;
  }
`;

const CountNumber = styled.span`
  font-size: 32px;
  font-weight: 700;
  color: ${colors.primary.main};
  line-height: 1;
`;

const CountLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.neutral[600]};
`;

const SearchSection = styled.div`
  flex: 1;
  min-width: 250px;
`;

const SecondRow = styled.div`
  display: flex;
  align-items: left;
  justify-content: left;
  gap: 10px;
  width: 60%;

  @media screen and (max-width: 1200px) {
    flex-wrap: wrap;
  }
`;

const StatusPickerSection = styled.div`
  display: flex;
  align-items: left;
  justify-content: left;
  min-width: 100px;

  @media screen and (max-width: 1200px) {
    width: 100%;
  }
`;

const FiltersSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  @media screen and (max-width: 1200px) {
    flex-wrap: wrap;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 150px;
`;
